import { useState, useEffect, useCallback } from 'react';
import { request } from '../../../shared/services/api';

type FichaTabla =
  | 'involucrados'
  | 'entradas'
  | 'salidas'
  | 'reportes'
  | 'requisitos'
  | 'herramientas'
  | 'indicadores';

interface FichaItem {
  id: string;
  [key: string]: unknown;
}

interface UseFichaEditReturn<T extends FichaItem> {
  items: T[];
  loading: boolean;
  error: string | null;
  crear: (data: Partial<T>) => Promise<T | null>;
  actualizar: (id: string, data: Partial<T>) => Promise<T | null>;
  eliminar: (id: string) => Promise<boolean>;
  refetch: () => void;
}

export function useFichaEdit<T extends FichaItem>(
  procesoId: string | null,
  tabla: FichaTabla,
): UseFichaEditReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseUrl = procesoId ? `/procesos/${procesoId}/ficha/${tabla}` : null;

  const fetchItems = useCallback(() => {
    if (!baseUrl) return;
    setLoading(true);
    setError(null);
    request<{ data: T[] }>(baseUrl)
      .then((res) => setItems(res.data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, [baseUrl]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const crear = useCallback(
    async (data: Partial<T>): Promise<T | null> => {
      if (!baseUrl) return null;
      try {
        const res = await request<{ data: T }>(baseUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        setItems((prev) => [...prev, res.data]);
        return res.data;
      } catch {
        return null;
      }
    },
    [baseUrl],
  );

  const actualizar = useCallback(
    async (id: string, data: Partial<T>): Promise<T | null> => {
      if (!baseUrl) return null;
      try {
        const res = await request<{ data: T }>(`${baseUrl}/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        setItems((prev) => prev.map((item) => (item.id === id ? res.data : item)));
        return res.data;
      } catch {
        return null;
      }
    },
    [baseUrl],
  );

  const eliminar = useCallback(
    async (id: string): Promise<boolean> => {
      if (!baseUrl) return false;
      try {
        await request<void>(`${baseUrl}/${id}`, { method: 'DELETE' });
        setItems((prev) => prev.filter((item) => item.id !== id));
        return true;
      } catch {
        return false;
      }
    },
    [baseUrl],
  );

  return { items, loading, error, crear, actualizar, eliminar, refetch: fetchItems };
}
